'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AlertCircle, Filter, RefreshCw, Search } from 'lucide-react'
import { Input } from '@/components/ui/input'
import Turnstile, { type TurnstileHandle } from './Turnstile'
import { ResizableTable, TableTd, type ColumnDef } from './ResizableTable'
import { SparkCard } from './charts'
import { BeneficiaryRankingCard, type BeneficiaryRankItem } from './SibsMailView'
import { BaseDashboard, baseDashboardClasses } from './BaseDashboard'
import { EntryDetailShell, type DetailBadge } from './EntryDetail/EntryDetailShell'
import { ProcessedEntryTimeline } from './ProcessedEntry/DomainEntry/ProcessedEntryTimeline'
import { OriginalSubmissionWidget } from './ProcessedEntry/OriginalSubmissionWidget'
import { LatestObtainedImagesPanel } from './ProcessedEntry/DomainEntry/LatestObtainedImagesPanel'
import { DomainIntelligencePanel } from './ProcessedEntry/DomainEntry/DomainIntelligencePanel'
import { BrowserDataPanel } from './ProcessedEntry/DomainEntry/BrowserDataPanel'
import { ProbeRunsPanel } from './ProcessedEntry/DomainEntry/ProbeRunsPanel'
import { PipelineStepsWidget } from './PipelineStepsWidget'
import type { ProcessedEntry, ProcessedTheme, BrowserRow, EntryDetailData } from './ProcessedEntry/DomainEntry/processedTypes'
import { compactDate } from './ProcessedEntry/DomainEntry/processedTypes'
import { parseDbTimestamp } from '@/lib/dates'

const TURNSTILE_SITEKEY = process.env.NEXT_PUBLIC_CLOUDFLARE_TURNSTILE_TF_CENTER_GETENTRIES_KEY ?? ''

const CTT_MAIL_COLUMNS: ColumnDef[] = [
  { key: 'user_entry_id', label: 'User_Entry_ID', initialWidth: 120 },
  { key: 'created_at', label: 'Created_At', initialWidth: 130 },
  { key: 'post_type', label: 'Post_Type', initialWidth: 100 },
  { key: 'source_type', label: 'Source_Type', initialWidth: 120 },
  { key: 'sender', label: 'Sender', initialWidth: 160 },
  { key: 'message_content', label: 'Message_Content', initialWidth: 320 },
  { key: 'domain', label: 'Domain', initialWidth: 200 },
]

// Ranks arbitrary string-keyed groups by occurrence count — same shape SIBS's
// beneficiary-ranking RPCs produce (BeneficiaryRankItem). Computed
// client-side rather than via a dedicated RPC: those SIBS RPCs live only in
// Supabase (no in-repo migrations to model CTT equivalents on), so this
// reads from the same /api/campaigns/processed data every other CTT
// processed-entries view already uses and aggregates it here instead.
function rankBy(entries: ProcessedEntry[], getKey: (e: ProcessedEntry) => string | null | undefined): BeneficiaryRankItem[] {
  const counts = new Map<string, number>()
  for (const e of entries) {
    const key = getKey(e)?.trim()
    if (!key) continue
    counts.set(key, (counts.get(key) ?? 0) + 1)
  }
  return Array.from(counts.entries())
    .sort(([, a], [, b]) => b - a)
    .map(([key, count], i) => ({ key, label: key, count, rank: i + 1 }))
}

function domainOf(e: ProcessedEntry): string | null {
  if (!e.originalUrl) return null
  try { return new URL(e.originalUrl).hostname } catch { return e.originalUrl }
}

// The "equivalent window" to SIBS's own SibsEntryDetailPage — same shell
// (EntryDetailShell, mode="page", full-page swap over the dashboard rather
// than a modal) and the same domain-intelligence fetch pattern. The widgets
// inside are ported straight from "Old Processed Entries"' single-entry
// detail view (ProcessedEntryCard's expanded/detailMode section) rather
// than rebuilt — they're already standalone, reusable components (each
// wraps itself in the shared Widget card), so this just assembles them in
// the same grid shape ProcessedEntryCard already uses. Not yet ported: that
// view's evidence-summary/takedown/"Create brand campaign" action section —
// left for a follow-up once the intelligence widgets themselves are settled
// here.
function CttEntryDetailPage({
  entry,
  theme,
  onClose,
  getScreenshotToken,
}: {
  entry: ProcessedEntry
  theme: ProcessedTheme
  onClose: () => void
  getScreenshotToken: () => Promise<string>
}) {
  const [data, setData] = useState<EntryDetailData | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!entry.userEntryDomainId) return
    setError(null)
    fetch(`/api/campaigns/ctt/entry-domains?userEntryDomainId=${entry.userEntryDomainId}`)
      .then((r) => r.json())
      .then((res) => {
        if (res.success) setData(res.data)
        else setError(res.reason ?? 'Failed to load domain details')
      })
      .catch(() => setError('Network error loading domain details'))
  }, [entry.userEntryDomainId])

  const curDomain = data?.domains.find((d) => d.id === entry.userEntryDomainId) ?? data?.domains[0] ?? null
  const curBrowserData = data?.browserData.filter((r) => r.entryDomainId === entry.userEntryDomainId) ?? []
  const latestBrowser = curBrowserData.reduce<BrowserRow | null>((latest, row) =>
    !latest || new Date(row.createdAt).getTime() > new Date(latest.createdAt).getTime() ? row : latest, null)
  const curProbeRuns = (data?.probeRuns.filter((r) => r.entryDomainId === entry.userEntryDomainId) ?? [])
    .sort((a, b) => {
      const nodeCompare = (a.probeNode ?? '').localeCompare(b.probeNode ?? '')
      if (nodeCompare !== 0) return nodeCompare
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    })

  const badges: DetailBadge[] = entry.isScam ? [{ label: 'scam', tone: 'red' }] : []

  return (
    <EntryDetailShell
      mode="page"
      open
      onClose={onClose}
      theme={theme}
      cols={3}
      title={entry.headline ?? entry.title ?? 'Entry'}
      subtitle={compactDate(entry.createdAt)}
      badges={badges}
      gridRows="280px 280px 280px"
    >
      {/* Widgets render immediately rather than waiting on the domain-intel
          fetch to finish — they already handle a null domain / empty
          browser-data / probe-run arrays gracefully (same props
          ProcessedEntryCard passes them while its own equivalent fetch is
          in flight), so there's no reason to blank the whole page behind
          one big loading gate first. error/no-domain still show as a small
          inline note rather than blocking the grid. */}
      {error && <div className="col-span-3 font-mono text-xs text-destructive">{error}</div>}
      {!entry.userEntryDomainId && <div className="col-span-3 font-mono text-xs uppercase tracking-[0.28em] text-muted-foreground">No domain associated</div>}
      <ProcessedEntryTimeline entry={entry} domain={curDomain} browserData={curBrowserData} theme={theme} />
      <OriginalSubmissionWidget
        r2Key={entry.rawEntryR2Key}
        theme={theme}
        getToken={getScreenshotToken}
        postType={entry.postType}
        messageTypeId={entry.messageTypeId}
        messageTypeText={entry.messageTypeName}
        className="col-span-1 row-span-1 h-full"
      />
      <LatestObtainedImagesPanel
        entryId={entry.id}
        userEntryDomainId={entry.userEntryDomainId}
        latestBrowser={latestBrowser}
        theme={theme}
        getToken={getScreenshotToken}
      />
      <DomainIntelligencePanel entry={entry} domain={curDomain} theme={theme} />
      <BrowserDataPanel curBrowserData={curBrowserData} latestBrowser={latestBrowser} theme={theme} />
      <ProbeRunsPanel curProbeRuns={curProbeRuns} theme={theme} />
      <PipelineStepsWidget
        steps={entry.pipelineSteps ?? entry.pipeline_steps}
        theme={theme}
        className="col-span-3"
      />
    </EntryDetailShell>
  )
}

// CTT's Custom Mail Alert dashboard — built on the same BaseDashboard shell
// SIBS's own Custom Mail Alert view is modeled on, with SparkCard/
// BeneficiaryRankingCard bento widgets in place of SIBS's beneficiary-
// specific ones (CTT's fraud is phishing SMS/email impersonating CTT
// delivery/customs notices, not bank-transfer beneficiaries, so "top
// spoofed senders" / "top target domains" are the analogous rankings here).
export function CttMailAlertView({ theme }: { theme: ProcessedTheme }) {
  const { isDark, muted } = baseDashboardClasses(theme)

  const [entries, setEntries] = useState<ProcessedEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [lastUpdated, setLastUpdated] = useState<number | null>(null)
  const [query, setQuery] = useState('')
  const [detailEntry, setDetailEntry] = useState<ProcessedEntry | null>(null)
  const turnstileRef = useRef<TurnstileHandle>(null)
  const screenshotTurnstileRef = useRef<TurnstileHandle>(null)
  const pendingTokenRef = useRef<((token: string) => void) | null>(null)

  const handleScreenshotVerify = useCallback((token: string) => {
    pendingTokenRef.current?.(token)
    pendingTokenRef.current = null
  }, [])

  const getScreenshotToken = useCallback((): Promise<string> => {
    return new Promise((resolve) => {
      pendingTokenRef.current = resolve
      const runWhenReady = (attempt = 0) => {
        if (screenshotTurnstileRef.current) {
          screenshotTurnstileRef.current.reset()
          screenshotTurnstileRef.current.run()
          return
        }
        if (attempt < 20) setTimeout(() => runWhenReady(attempt + 1), 100)
      }
      runWhenReady()
    })
  }, [])

  const handleVerify = useCallback(async (token: string) => {
    setError(null)
    try {
      const res = await fetch('/api/campaigns/processed', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ turnstileToken: token, platformCode: 'ctt' }),
      })
      const payload = await res.json()
      if (payload?.success) {
        setEntries((payload.data.entries ?? []).filter((e: ProcessedEntry) => e.postType === 'text'))
        setLastUpdated(Date.now())
      } else {
        setError(payload?.reason ?? payload?.error ?? 'Failed to load data')
      }
    } catch {
      setError('Network error')
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [])

  const handleTurnstileError = useCallback(() => {
    setLoading(false)
    setRefreshing(false)
    setError('Verification failed — try refreshing')
  }, [])

  const handleRefresh = useCallback(() => {
    setRefreshing(true)
    setError(null)
    turnstileRef.current?.reset()
    turnstileRef.current?.run()
  }, [])

  const filtered = useMemo(
    () =>
      entries.filter((e) => {
        if (!query) return true
        const q = query.toLowerCase()
        return (
          (e.headline ?? '').toLowerCase().includes(q) ||
          (e.messageContent ?? '').toLowerCase().includes(q) ||
          (e.sender ?? '').toLowerCase().includes(q)
        )
      }),
    [entries, query],
  )

  const scamCount = useMemo(() => entries.filter((e) => e.isScam).length, [entries])
  const legitCount = entries.length - scamCount
  const fraudRatio = entries.length > 0 ? Math.round((scamCount / entries.length) * 100) : 0

  const dailyData = useMemo(() => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const dayMs = 86400000
    const map = new Map<string, { scam: number; legit: number }>()
    for (let i = 0; i < 30; i++) {
      const d = new Date(today.getTime() - (29 - i) * dayMs)
      map.set(`${d.getDate()}/${d.getMonth() + 1}`, { scam: 0, legit: 0 })
    }
    for (const e of entries) {
      const d = parseDbTimestamp(e.createdAt)
      if (!d) continue
      const key = `${d.getDate()}/${d.getMonth() + 1}`
      const bucket = map.get(key)
      if (bucket) bucket[e.isScam ? 'scam' : 'legit']++
    }
    return Array.from(map.entries()).map(([date, v]) => ({ date, ...v }))
  }, [entries])

  const todayCount = useMemo(() => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    return entries.filter((e) => {
      const d = parseDbTimestamp(e.createdAt)
      return d !== null && d >= today
    }).length
  }, [entries])

  const topSenders = useMemo(() => rankBy(entries, (e) => e.sender), [entries])
  const topDomains = useMemo(() => rankBy(entries, domainOf), [entries])

  const fmtDate = (iso: string) => {
    const d = parseDbTimestamp(iso)
    if (!d) return '—'
    const time = new Intl.DateTimeFormat('pt-PT', { hour: '2-digit', minute: '2-digit' }).format(d)
    const date = new Intl.DateTimeFormat('pt-PT', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(d)
    return `${time} ${date}`
  }

  // turnstileRef's widget must stay mounted for the component's whole
  // lifetime — it used to only render during the `loading` branch below, so
  // handleRefresh's turnstileRef.current?.run() silently no-op'd (ref was
  // null) on every refresh/auto-refresh after the first load: no new token
  // ever got requested, handleVerify never re-ran, and the table just sat
  // there looking refreshed (refreshing flipped true and stuck, since only
  // handleVerify's finally clears it) without ever re-fetching. Hoisting it
  // above the loading/error/detailEntry/main branches keeps one stable
  // instance across all of them — safe since it's `size: 'invisible'`
  // (Turnstile.tsx), no visual footprint regardless of which branch is
  // showing.
  return (
    <>
      <Turnstile ref={turnstileRef} sitekey={TURNSTILE_SITEKEY} onVerify={handleVerify} onError={handleTurnstileError} onTimeout={handleTurnstileError} autoRun />
      {loading ? (
        <div className="flex h-64 items-center justify-center">
          <span className={`font-mono text-[11px] uppercase tracking-widest animate-pulse ${muted}`}>Loading…</span>
        </div>
      ) : error ? (
        <div className={`flex h-64 items-center justify-center gap-3 text-destructive`}>
          <AlertCircle className="h-5 w-5" />
          <span className="font-mono text-[13px]">{error}</span>
        </div>
      ) : detailEntry ? (
        <>
          <Turnstile ref={screenshotTurnstileRef} sitekey={TURNSTILE_SITEKEY} onVerify={handleScreenshotVerify} />
          <CttEntryDetailPage
            entry={detailEntry}
            theme={theme}
            onClose={() => setDetailEntry(null)}
            getScreenshotToken={getScreenshotToken}
          />
        </>
      ) : (
      <BaseDashboard
        theme={theme}
        eyebrow="CTT · Custom Mail Alert"
        title="Custom Mail Alert"
        subtitle="Reports received via alertsctt@mail.trustfence.ai, run through the same classification pipeline as any other submission."
        onRefresh={handleRefresh}
        refreshing={refreshing}
        lastUpdated={lastUpdated}
        storageKey="ctt-mail-alert-header"
        widgets={
          <>
            <SparkCard
              id="ctt-mail-inrange"
              label="In range"
              value={entries.length}
              sub1={{ label: 'Scam', value: scamCount, color: 'var(--destructive)' }}
              sub2={{ label: 'Legit', value: legitCount, color: 'var(--signal)' }}
              trend={todayCount > 0 ? `+${todayCount} today` : undefined}
              chartData={dailyData}
              series={[{ key: 'scam', color: 'var(--destructive)' }, { key: 'legit', color: 'var(--signal)' }]}
              isDark={isDark}
              className="col-span-3 h-full"
            />
            <SparkCard
              id="ctt-mail-scam"
              label="Fraudulent"
              value={scamCount}
              trend={entries.length > 0 ? `${fraudRatio}%` : undefined}
              chartData={dailyData}
              series={[{ key: 'scam', color: 'var(--destructive)' }]}
              isDark={isDark}
              className="col-span-3 h-full"
            />
            <SparkCard
              id="ctt-mail-legit"
              label="Legitimate"
              value={legitCount}
              chartData={dailyData}
              series={[{ key: 'legit', color: 'var(--signal)' }]}
              isDark={isDark}
              className="col-span-3 h-full"
            />
            <SparkCard
              id="ctt-mail-ratio"
              label="Fraud ratio"
              value={fraudRatio}
              valueSuffix="%"
              sub1={{ label: 'Fraudulent', value: scamCount, color: 'var(--destructive)' }}
              sub2={{ label: 'Legitimate', value: legitCount, color: 'var(--signal)' }}
              chartData={dailyData}
              series={[{ key: 'scam', color: '#f43f5e' }]}
              isDark={isDark}
              className="col-span-3 h-full"
            />
            <BeneficiaryRankingCard
              eyebrow="Senders"
              title="Top spoofed senders"
              items={topSenders}
              accent="#a855f7"
              isDark={isDark}
              className="col-span-6 row-start-2 h-full"
              limit={8}
              emptyLabel="No senders in range"
            />
            <BeneficiaryRankingCard
              eyebrow="Domains"
              title="Top target domains"
              items={topDomains}
              accent="var(--warning)"
              isDark={isDark}
              className="col-span-6 row-start-2 h-full"
              limit={8}
              emptyLabel="No domains in range"
            />
          </>
        }
      >
        <ResizableTable
          columns={CTT_MAIL_COLUMNS}
          stickyHeader
          theme={theme}
          isEmpty={filtered.length === 0}
          emptyMessage="No entries found"
          onRefresh={handleRefresh}
          refreshing={refreshing}
          lastUpdated={lastUpdated}
          storageKey="ctt-mail-alert-table"
          toolbar={
            <>
              <Search className={`h-3.5 w-3.5 shrink-0 ${muted}`} />
              <Input
                placeholder="Filter by title, sender, message…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="h-7 border-0 shadow-none bg-transparent focus-visible:ring-0 px-0 text-[13px]"
              />
              <Filter className={`h-3.5 w-3.5 shrink-0 ${muted}`} />
              <span className={`font-mono text-[10px] uppercase tracking-widest whitespace-nowrap ${muted}`}>
                {filtered.length} / {entries.length}
              </span>
            </>
          }
        >
          {filtered.map((e) => (
            <tr
              key={e.id}
              onClick={() => setDetailEntry(e)}
              className={`border-b border-l-2 transition-colors cursor-pointer border-border ${
                e.isScam
                  ? 'border-l-destructive bg-danger-surface hover:bg-danger-surface/70'
                  : 'border-l-transparent hover:border-l-signal hover:bg-secondary'
              }`}
            >
              <TableTd className={`font-mono truncate text-[10px] text-muted-foreground`}>{e.userEntryId ?? '—'}</TableTd>
              <TableTd className={`font-mono whitespace-nowrap ${muted}`}>{fmtDate(e.createdAt)}</TableTd>
              <TableTd className={`font-mono text-muted-foreground`}>{e.postType ?? '—'}</TableTd>
              <TableTd className={`font-mono truncate text-muted-foreground`}>{e.sourceType ?? '—'}</TableTd>
              <TableTd className={'text-muted-foreground'}>{e.sender ?? '—'}</TableTd>
              <TableTd><span className={`line-clamp-1 text-muted-foreground`}>{e.messageContent ?? '—'}</span></TableTd>
              <TableTd className={`font-mono truncate text-muted-foreground`}>{domainOf(e) ?? '—'}</TableTd>
            </tr>
          ))}
        </ResizableTable>
      </BaseDashboard>
      )}
    </>
  )
}
